import { createContext, useContext, useMemo } from "react";
import { BusContext } from "context";
import { useSelection } from "hooks";
import { Document } from "types";
import { useParams } from "react-router-dom";
import { useDocuments } from "hooks/queries/useDocuments";

type SelectionState = {
    selection: Document[],
    selectionIds: string[],
    setSelection: (selection: Document[]) => void,
    selectNext: () => void,
    selectPrevious: () => void,

}
const defaultSelectionState: SelectionState = {
    selection: [],
    selectionIds: [],
    setSelection: () => { },
    selectNext: () => { },
    selectPrevious: () => { },
}
export const SelectionContext = createContext<SelectionState>(defaultSelectionState);

type SelectionProviderProps = {
    children: JSX.Element,
}

interface UseDocumentsResult {
    documents: Document[];
    count: number;
    isLoading: boolean;
    isError: boolean;
}

export function SelectionProvider({ children }: SelectionProviderProps) {
    const { workspaceId, viewId } = useParams<{ workspaceId: string; viewId: string }>();
    const { select, doSelect } = useContext(BusContext);

    const result: UseDocumentsResult = useDocuments(workspaceId, viewId) || {
        documents: [],
        count: 0,
        isLoading: false,
        isError: false,
    };

    const selectionIds = select?.tags ?? [];
    const selection = useMemo(() =>
        (select?.tags ?? [])
            .map(x => (result.documents || [])
                .find(y => y.id === x))
        , [select?.tags, result.documents]);

    const items = useMemo(() => (result.documents || []), [result.documents]);

    const { getNext, getPrevious } = useSelection<Document>({ items, selectedItems: selection });

    return (
        <SelectionContext.Provider value={{
            selection,
            selectionIds,
            setSelection,
            selectPrevious: () => selectUsing(getPrevious),
            selectNext: () => selectUsing(getNext),
        }}>
            {children}
        </SelectionContext.Provider>
    );

    function setSelection(newSelection: Document[]) {
        doSelect({ tags: newSelection.map(x => x.id) });
    }

    function selectUsing(getAction: () => Document) {
        const newItem = getAction();
        if (newItem)
            setSelection([newItem]);
    }
}

export default SelectionProvider;
